<script lang="ts">
    import { onMount } from 'svelte'
    import Zframe from '../../_common/z/zframe.svelte'
    import dayjs from 'dayjs'
    import type { Goalboard } from '../../../modules/toolsApi/goalboards/goalboard.entity'
    import type { EventSource } from '../../../modules/eventsApi/event-sources/event-source.entity'
    import type { Service } from '../../../modules/posApi/services/service'
    import type { Invoice } from '../../../modules/posApi/invoices/invoice'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import { isFullscreen } from '../../_common/stores'
    import type { Channel } from '../../../modules/brokerApi/channels/channel'
    import Content from '../../_common/content.svelte'

    let fullscreen = false
    isFullscreen.subscribe((value) => {
        fullscreen = value
    })

    interface iFrame {
        goal: Goalboard
        selectedDate: string
        periodCurrent: number
        periodTotal: number
        contacted: number
        scheduled: number
        show: number
        showPercentual: number
        converted: number
        convertedPercentual: number
        convertedPartial: Date
        timeoutHandle: any
    }

    let frames: iFrame[] = []
    let consulting: string[] = []
    let channels: Channel[] = []
    let show = false

    async function loadChannels() {
        channels = await window.api.brokers.channels.getAll()
    }

    async function loadConsultingServices() {
        const services: Service[] = await window.api.pos.services.getAll({ consulting: true })
        consulting = services.map((el) => el.serviceId)
    }

    onMount(async () => {
        await loadChannels()
        await loadConsultingServices()
        await loadFrames()
    })

    function getHTMLCurrentDate(days: number) {
        if (days === 30) return dayjs(new Date()).format('YYYY-MM')
        if (days === 15) return dayjs(new Date()).format('YYYY-MM-DD')
        if (days === 1) return dayjs(new Date()).format('YYYY-MM-DD')
        let date = new Date()
        const daysPast = dayjs(new Date()).diff(new Date(new Date().getFullYear(), 0, 1), 'day')
        const stringDate = date.getFullYear() + '-W' + Math.ceil(daysPast / 7)
        return stringDate
    }

    function getDates(frame): { from: Date; to: Date } {
        let selectedDate = null
        if (frame.selectedDate.includes('-W')) {
            const tempDate = new Date(parseInt(frame.selectedDate.split('-')[0]), 0, 1)
            tempDate.setDate(parseInt(frame.selectedDate.split('-W')[1]) * 7 - 7)
            selectedDate = new Date(tempDate)
        } else if (frame.selectedDate.split('-').length === 2) {
            selectedDate = new Date(frame.selectedDate + '-05')
        } else {
            selectedDate = new Date(frame.selectedDate)
        }
        let from: Date = null
        let to: Date = null
        // se diário
        if (frame.goal.goalWindowDays === 1) {
            from = new Date(selectedDate)
            to = new Date(from)
            to.setDate(to.getDate() + 1)
        }
        if (frame.goal.goalWindowDays === 7) {
            from = new Date(selectedDate)
            from.setDate(from.getDate() - from.getDay())
            to = new Date(from)
            to.setDate(to.getDate() + 7)
        }
        // quinzenal
        if (frame.goal.goalWindowDays === 15) {
            from = new Date(selectedDate)
            if (from.getDate() <= 15) {
                from.setDate(1)
                to = new Date(from)
                to.setDate(15)
            } else {
                from.setDate(15)
                to = new Date(from.getFullYear(), from.getMonth() + 1, 0)
            }
        }
        // mensal
        if (frame.goal.goalWindowDays === 30) {
            from = new Date(selectedDate)
            from.setDate(1)
            to = new Date(from.getFullYear(), from.getMonth() + 1, 0)
        }

        return { from, to }
    }

    async function getOldCustomers(personId, from, to) {
        let promises = []
        for (let i = 0; i < personId.length; i += 100) {
            const chunk = personId.slice(i, i + 100)
            promises.push(
                window.api.pos.invoices.getAll(
                    {
                        from: from,
                        to: to,
                        personId: chunk.join(','),
                        service: false,
                        payment: false,
                    },
                    3600,
                ),
            )
        }
        const results = await Promise.all(promises)
        let oldCustomers: Invoice[] = []
        for (const result of results) {
            oldCustomers = oldCustomers.concat(result)
        }
        return oldCustomers
    }

    async function loadFrames() {
        frames = []
        let goals: Goalboard[] = await window.api.tools.goalboards.getAll()
        console.log('goals', goals)
        for (const goal of goals) {
            let selectedDate = getHTMLCurrentDate(goal.goalWindowDays)
            frames.push({
                periodCurrent: 0,
                periodTotal: 0,
                contacted: 0,
                converted: 0,
                selectedDate: selectedDate,
                goal: goal,
                scheduled: 0,
                show: 0,
                showPercentual: 0,
                convertedPercentual: 0,
                convertedPartial: null,
                timeoutHandle: null,
            })
            updateFrame(frames.length - 1)
        }
    }

    async function updateFrame(index: number) {
        if (!fullscreen) show = true
        let frame = frames[index]

        let { from, to } = getDates(frame)

        const startDate = new Date(from)
        const endDate = new Date(to)
        startDate.setMinutes(startDate.getMinutes() - window.store.company.timezone)
        endDate.setMinutes(endDate.getMinutes() - window.store.company.timezone)
        frame.periodTotal = 0
        frame.periodCurrent = 0
        const now = new Date()
        now.setHours(0, 0, 0, 0)
        while (startDate.getTime() <= endDate.getTime()) {
            if (frame.goal.workingDays.includes(startDate.getDay())) {
                frame.periodTotal += 1
                let diff = dayjs(now).diff(startDate, 'day')
                if (diff >= 0) {
                    frame.periodCurrent += 1
                }
            }
            startDate.setDate(startDate.getDate() + 1)
        }

        await updateComunication(frame, from, to)
        await updateSchedulers(frame, from, to)
        await updateConversions(frame, from, to)
        frames = frames

        if (frame.timeoutHandle) clearTimeout(frame.timeoutHandle)
        if (frame.goal.refreshTime > 0) {
            let refreshTime = frame.goal.refreshTime * 1000 * 60 + index * 10000
            frame.timeoutHandle = setTimeout(async () => await updateFrame(index), refreshTime)
        }
        show = false
    }

    async function updateComunication(frame: iFrame, from, to) {
        let params = {
            from: new Date(from),
            to: new Date(to),
            sources: ['whatsapp', 'voip', 'telefone', 'sms', 'email'],
        }
        let events: EventSource[] = await window.api.events.eventSources.getAllFiltered(params)
        for (const es of events) {
            es.channel = channels.find((el) => el.channelId === es.channelId)
        }
        console.log('comunications', events)
        let personId = events.map((el) => el.personId)
        let oldFrom = new Date(from)
        oldFrom.setMonth(oldFrom.getMonth() - 18)
        let oldCustomers: Invoice[] = await getOldCustomers(personId, oldFrom, from)
        // todo: remove from "events" active customers with schedules in last 90 days
        frame.contacted = 0
        let persons = []
        for (const es of events) {
            es.externalDate = new Date(es.externalDate)
            if (frame.goal.users.length === 0 || frame.goal.users.includes(es.channel?.userId)) {
                if (
                    ((es.source === 'voip' || es.source === 'celular' || es.source === 'telefone') &&
                        (es.lastEvent === 'comunicacao_enviada' || es.lastEvent === 'comunicacao_recebida')) ||
                    es.lastEvent === 'comunicacao_enviada'
                ) {
                    if (oldCustomers.some((el) => el.personId === es.personId)) continue
                    if (persons.includes(es.personId)) continue
                    persons.push(es.personId)
                    frame.contacted += 1
                }
            }
        }
    }

    async function updateSchedulers(frame: iFrame, from, to) {
        const scheds = await window.api.schedulers.appointments.getConsulting(from, to, consulting, true)
        let personsScheduled = []
        let personsShow = []
        frame.scheduled = 0
        frame.show = 0
        for (const sched of scheds) {
            if (frame.goal.users.length === 0 || frame.goal.users.includes(sched.appointment_userId)) {
                if (!personsScheduled.includes(sched.personId)) {
                    personsScheduled.push(sched.personId)
                    frame.scheduled += 1
                }
                if (sched.status === 'agendamento_compareceu') {
                    if (!personsShow.includes(sched.personId)) {
                        personsShow.push(sched.personId)
                        frame.show += 1
                    }
                }
            }
        }
        frame.showPercentual = frame.scheduled === 0 ? 0 : parseInt(((100 / frame.scheduled) * frame.show).toFixed(0))
    }

    async function updateConversions(frame: iFrame, from, to) {
        const toWindow = new Date(to)
        toWindow.setDate(toWindow.getDate() + frame.goal.conversionWindowDays)
        frame.convertedPartial = dayjs(toWindow).diff(new Date(), 'day') > 0 ? new Date(toWindow) : null
        const scheds: any[] = await window.api.schedulers.appointments.getConsulting(from, toWindow, consulting, true)
        const invoices: Invoice[] = await window.api.pos.invoices.getAll({ from, to, service: true })
        let persons: string[] = []
        frame.converted = 0
        for (const invoice of invoices) {
            for (const item of invoice.services) {
                for (const sched of scheds) {
                    if (sched.personId !== invoice.personId) continue
                    if (frame.goal.users.length !== 0 && !frame.goal.users.includes(item.userId)) continue
                    const daysDiff = dayjs(invoice.createdAt).diff(sched.start, 'day')
                    if (daysDiff > frame.goal.conversionWindowDays) continue
                    if (persons.includes(invoice.personId)) continue
                    persons.push(invoice.personId)
                    frame.converted += 1
                }
            }
        }
        frame.convertedPercentual = frame.show === 0 ? 0 : parseInt(((100 / frame.show) * frame.converted).toFixed(0))
    }

    function calculateColor(goal, current, periodCurrent, periodTotal) {
        // todo: deve-se levar em consideração os dias no período e os dias trabalhados
        const partialGoal = (goal / periodTotal) * periodCurrent
        const value = current / partialGoal
        if (value < 1) return 'goal_danger'
        if (value < 1.1) return 'goal_alert'
        return 'goal_success'
    }

    function getCalendarType(range) {
        if (range === 30) return 'month'
        if (range === 7) return 'week'
        return 'date'
    }
</script>

<Content footer={true}>
    <LoadSpinner {show} delay={100} />
    {#each frames as frame, index}
        {@const type = getCalendarType(frame.goal.goalWindowDays)}
        <Zframe class="goals_frame mt-2 mb-2" title={frame.goal.label}>
            {#if type === 'month'}
                <input type="month" bind:value={frame.selectedDate} on:change={() => updateFrame(index)} class="form-control form-control-sm frame_date" />
            {/if}
            {#if type === 'week'}
                <input type="week" bind:value={frame.selectedDate} on:change={() => updateFrame(index)} class="form-control form-control-sm frame_date" />
            {/if}
            {#if type === 'date'}
                <input type="date" bind:value={frame.selectedDate} on:change={() => updateFrame(index)} class="form-control form-control-sm frame_date" />
            {/if}
            <div class="goal">
                <div class="goal_box {calculateColor(frame.goal.contactedGoal, frame.contacted, frame.periodCurrent, frame.periodTotal)}">
                    <div class="box_title">Pessoas contactadas</div>
                    <div class="box_number">{frame.contacted}</div>
                    <div class="box_footer">
                        <div class="box_footer_meta">Meta {frame.goal.contactedGoal}</div>
                        <div class="box_footer_meta">
                            Parcial {Math.ceil((frame.goal.contactedGoal / frame.periodTotal) * frame.periodCurrent)}
                        </div>
                    </div>
                </div>
                <div class="goal_box {calculateColor(frame.goal.scheduledGoal, frame.scheduled, frame.periodCurrent, frame.periodTotal)}">
                    <div class="box_title">Pessoas agendadas</div>
                    <div class="box_number">{frame.scheduled}</div>
                    <div class="box_footer">
                        <div class="box_footer_meta">Meta {frame.goal.scheduledGoal}</div>
                        <div class="box_footer_meta">
                            Parcial {Math.ceil((frame.goal.scheduledGoal / frame.periodTotal) * frame.periodCurrent)}
                        </div>
                    </div>
                </div>
                {#if frame.goal.resultType === 'relative'}
                    <div class="goal_box {calculateColor(frame.goal.showGoal, frame.showPercentual, frame.periodTotal, frame.periodTotal)}">
                        <div class="box_title">Comparecimento</div>
                        <div class="box_number">{frame.showPercentual}%</div>
                        <div class="box_footer">
                            <div class="box_footer_meta">Meta {frame.goal.showGoal}%</div>
                            <div class="box_footer_meta">Compareceu {frame.show}</div>
                        </div>
                    </div>
                    <div
                        class="goal_box {!frame.convertedPartial
                            ? calculateColor(frame.goal.conversionGoal, frame.convertedPercentual, frame.periodTotal, frame.periodTotal)
                            : ''}"
                    >
                        <div class="box_title">Conversão</div>
                        <div class="box_number">{frame.convertedPercentual}%</div>
                        <div class="box_footer">
                            <div class="box_footer_meta">Meta {frame.goal.conversionGoal}%</div>
                            <div class="box_footer_meta">Converteu {frame.converted}</div>
                        </div>
                    </div>
                {:else}
                    <div class="goal_box {calculateColor(frame.goal.showGoal, frame.show, frame.periodTotal, frame.periodTotal)}">
                        <div class="box_title">Comparecimento</div>
                        <div class="box_number">{frame.show}</div>
                        <div class="box_footer">
                            <div class="box_footer_meta">Meta {frame.goal.showGoal}</div>
                            <div class="box_footer_meta">
                                Parcial {Math.ceil((frame.goal.showGoal / frame.periodTotal) * frame.periodCurrent)}
                            </div>
                        </div>
                    </div>
                    <div
                        class="goal_box {!frame.convertedPartial
                            ? calculateColor(frame.goal.conversionGoal, frame.converted, frame.periodTotal, frame.periodTotal)
                            : ''}"
                    >
                        <div class="box_title">Conversão</div>
                        <div class="box_number">{frame.converted}</div>
                        <div class="box_footer">
                            <div class="box_footer_meta">Meta {frame.goal.conversionGoal}</div>
                            <div class="box_footer_meta">
                                Parcial {Math.ceil((frame.goal.conversionGoal / frame.periodTotal) * frame.periodCurrent)}
                            </div>
                        </div>
                    </div>
                {/if}
            </div>
            {#if frame.convertedPartial}
                <div class="text-muted pt-2">
                    A taxa de conversão ainda é parcial pois a janela de conversão do período selecionado termina em {dayjs(frame.convertedPartial).format(
                        'DD/MM/YYYY',
                    )}
                </div>
            {/if}
        </Zframe>
    {/each}

    <div class="config-box my-2" class:h-all={fullscreen}>
        <div class="row help-list">
            <div class="help-item">
                <span class="badge">Pessoas contactadas:</span>
                <span class="help-text">todas as pessoas que NÃO realizaram compras nos últimos 18 meses e foram contactadas</span>
            </div>
            <div class="help-item">
                <span class="badge">Pessoas agendadas:</span>
                <span class="help-text">todas as pessoas que foram agendadas para um serviço cadastrado como "avaliação/orçamento"</span>
            </div>
            <div class="help-item">
                <span class="badge">Comparecimento:</span>
                <span class="help-text">todas as pessoas que compareceram para um serviço cadastrado como "avaliação/orçamento"</span>
            </div>
            <div class="help-item">
                <span class="badge">Conversão:</span>
                <span class="help-text">todas as pessoas que compareceram para um serviço cadastrado como "avaliação/orçamento" e depois compraram algo</span>
            </div>
            <div class="help-item">
                <span class="badge">Parcial:</span>
                <span class="help-text">representa a meta parcial proporcional à data atual</span>
            </div>
        </div>
    </div>
</Content>

<style>
    .help-list {
        font-size: 16px;
        opacity: 0.8;
        /* margin-bottom: 4px; */
    }
    .help-list > .help-item {
        margin-top: 2px !important;
        margin-bottom: 2px !important;
    }

    .help-list > .help-item .badge {
        background-color: #99999960;
        margin-right: 4px;
    }

    .goals_frame :global(.zblock_head) {
        padding-top: 0px;
        padding-bottom: 0px;
    }
    .goals_frame :global(.zblock_body) {
        padding-bottom: 6px;
    }
    .goals_frame :global(.zblock_title) {
        font-size: 32px !important;
    }

    .goal {
        display: flex;
        gap: 24px;
        flex-wrap: wrap;
    }

    .frame_date {
        max-width: 180px;
        margin-bottom: 8px;
    }

    .goal_box {
        border: 1px solid #99999910;
        flex: 1 1 10px;
        display: flex;
        flex-direction: column;
        background-color: #99999905;
        min-width: 250px;
    }

    .box_title {
        font-size: 31px;
        font-weight: 300;
        text-align: center;
        padding-top: 5px;
    }
    .box_number {
        font-size: 90px;
        text-align: center;
        flex-grow: 1;
        line-height: 100%;
        padding-bottom: 16px;
    }
    .box_footer {
        display: flex;
        background-color: #99999910;
        padding: 5px;
        font-size: 24px;
        justify-content: center;
    }
    .box_footer > * {
        flex-grow: 1;
        text-align: center;
    }

    .box_footer_meta {
    }

    .goal_alert {
        color: #ffc107;
    }

    .goal_danger {
        color: #dc3545;
    }

    .goal_success {
        color: #5dcb98;
    }
</style>
